import { CardActionBar } from "@/components/shared/CardActionBar";
import { LinkedCopiesSection } from "@/components/shared/LinkedCopiesSection";
import { TrashCardActionBar } from "@/components/shared/TrashCardActionBar";
import { useSelection } from "@/contexts/SelectionContext";
import {
  GenerateSlideSubtitlesDocument,
  GetLinkedItemInfoDocument,
  ModelEnum,
  RemoveSlideIntroductionVideoDocument,
  SetSlideIntroductionVideoDocument,
  SlideByIdDocument,
  SubtitleGenerationMode,
  SubtitleStatus,
  UpdateSlideSubtitleStyleDocument,
  type UpdateSlideSubtitleStyleMutation,
  type UpdateSlideSubtitleStyleMutationVariables,
  UpdateSlideSubtitlesDocument,
  UpdateSlideVideoTrimDocument,
  type UpdateSlideVideoTrimMutation,
  type UpdateSlideVideoTrimMutationVariables,
} from "@/generated/graphql";
import { SLIDE_DOMAINS } from "@/lib/constants/slides";
import { useEditSlideName } from "@/lib/hooks/slides/useEditSlideName";
import { getSlideWarning } from "@ai-teach/utils";

import type { SortableHandleProps } from "@/lib/dnd/components/Sortable";
import { getTemplateLabel } from "@/lib/slides/slideTemplates";
import { toastAiCredits } from "@/lib/utils/aiCredits";
import { getGraphQLErrorMessage, hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { saveScrollTarget } from "@/lib/utils/scrollRestoration";
import { canAbsorbSlide } from "@/lib/utils/subQuizMerge";
import { withToast } from "@/lib/utils/withToast";
import type { SlideFields } from "@/types/Slide";
import type { ViewMode } from "@/types/viewMode";
import type { WordSlideData } from "@ai-teach/types";
import { ErrorCodes, resolveSlideTemplate } from "@ai-teach/types";
import { Card, Checkbox, IconButton } from "@ai-teach/ui-web";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { type LinkProps, useNavigate } from "@tanstack/react-router";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { FaRegCalendarCheck } from "react-icons/fa";
import { FaPlay } from "react-icons/fa";
import { FiFilm, FiUploadCloud } from "react-icons/fi";
import { HiOutlineViewGrid, HiX } from "react-icons/hi";
import { IoWarning } from "react-icons/io5";
import {
  MdCheck,
  MdClose,
  MdDragIndicator,
  MdEdit,
  MdExpandLess,
  MdExpandMore,
  MdLink,
  MdVisibilityOff,
} from "react-icons/md";
import { GrammarFormPublicationBadge } from "../publication/GrammarFormPublicationBadge";
import { getGrammarFormPublicationState } from "../publication/grammarFormPublicationState";
import { SlideTagChips } from "./SlideTagChips";
import { SlideThumbnail } from "./SlideThumbnail";
import { SubtitleEditor } from "./SubtitleEditor";
import { type SubtitleStyleChange, VideoPlayer } from "./VideoPlayer";

/** Says a written slide is still waiting on its generated audio, so a silent card doesn't read as broken. */
function GeneratingAudioBadge({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className="flex items-center gap-1.5 rounded-full bg-primary-900/80 px-2.5 py-1 text-white shadow-md backdrop-blur-sm"
      title="Generating audio"
    >
      <span className="block h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
      {!compact && <span className="text-xs font-medium">Audio</span>}
    </div>
  );
}

interface SlideCardProps {
  slide: SlideFields;
  slideNumber: number;
  url: "trash" | "lesson";
  viewMode?: ViewMode;
  subslideCount?: number;
  isSubslidesExpanded?: boolean;
  onToggleSubslides?: () => void;
  isSubslide?: boolean;
  /** The slide exists but a generation job is still filling in its audio. */
  isGeneratingAudio?: boolean;
  /** Only this element starts a drag, so selecting text elsewhere on the card works normally. */
  dragHandle?: SortableHandleProps | undefined;
}

export const SlideCard: React.FC<SlideCardProps> = ({
  slide,
  slideNumber,
  url,
  viewMode = "grid",
  subslideCount = 0,
  isSubslidesExpanded = false,
  onToggleSubslides,
  isSubslide = false,
  isGeneratingAudio = false,
  dragHandle,
}) => {
  // Slides backfilled before `template` existed still resolve through the legacy type/knowledgeArea pair.
  const templateLabel = getTemplateLabel(resolveSlideTemplate(slide));

  // Stored order, not palette order — anything that needs a single domain takes the first, so it
  // is numbered once there is more than one to choose between.
  const domainLabels = (slide.domains ?? []).map(
    (domain) => SLIDE_DOMAINS.find((r) => r.enum === domain)?.label ?? domain
  );
  // An uncredited slide is invisible to the student report, so name the gap rather than blank it.
  const domainLabel =
    domainLabels.length > 1
      ? domainLabels.map((label, i) => `${i + 1}. ${label}`).join(", ")
      : (domainLabels[0] ?? "No domain");
  const domainColors = domainLabels.length
    ? "bg-violet-50 dark:bg-violet-950/30 border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300"
    : "bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-500";

  const navigate = useNavigate();

  const {
    selectedSlides,
    selectedSlideIds,
    toggleSlide,
    toggleWarningModal,
    mergeMasterId,
    mergeMasterIsFluencyReading,
    isMergeMode,
  } = useSelection();
  const isSelected = selectedSlideIds.includes(slide.id ?? "");
  const isSelectionMode = selectedSlideIds.length > 0;

  // Merge mode: the master slide is fixed (not selectable), and only slides the master can absorb
  // (standalone "other" always; standalone reading-fluency only into a fluency master) can be checked
  // as sources — everything else is disabled. `inSelectionUI` unifies the checkbox/action-bar gating so
  // normal selection mode behaves exactly as before when merge mode is off.
  const isMergeMaster = isMergeMode && mergeMasterId === slide.id;
  const mergeSelectDisabled =
    isMergeMode && (isMergeMaster || !canAbsorbSlide(slide, mergeMasterIsFluencyReading));
  const inSelectionUI = isMergeMode || isSelectionMode;

  // `selectedSlides` is insertion-ordered (unlike `selectedSlideIds`, which re-sorts by each slide's
  // list-position field) — its index is the true "picked 1st/2nd/3rd..." order the teacher built up.
  const mergeSelectionOrder =
    isMergeMode && !isMergeMaster
      ? selectedSlides.findIndex((item) => item.id === slide.id) + 1
      : 0;

  // Validation logic using getSlideWarning — a missing asset ("incomplete") OR invalid content ("error",
  // round 8: e.g. stray { } [ ] __ in grammar_focus) both flag the card and block it from students.
  const slideWarnings = getSlideWarning(slide);
  const hasIncomplete = slideWarnings.some((block) =>
    block.warningItem.some((item) => item.status === "incomplete" || item.status === "error")
  );

  // Grammar-form slides have an explicit publication bit (isCompleted) — draft/hidden/published state
  // is shown via a dedicated badge instead of the generic "Hidden" treatment used by other slide kinds.
  const isGrammarFormSlide = slide.template === "conversation";
  const publicationState = getGrammarFormPublicationState(slide);
  const showPublicationBadge = isGrammarFormSlide && publicationState !== "published";

  // Expandable warning state
  const [isWarningExpanded, setIsWarningExpanded] = useState(false);

  // Edit name state
  const [isEditingName, setIsEditingName] = useState(false);
  const [slideName, setSlideName] = useState(slide.name || "");

  // Sync slide name when slide prop changes
  useEffect(() => {
    if (!isEditingName && slide.name) {
      setSlideName(slide.name);
    }
  }, [slide.name, isEditingName]);

  // Show expand button if there are multiple items
  const totalItems = slideWarnings.reduce((sum, block) => sum + block.warningItem.length, 0);
  const showExpandButton = totalItems > 1;

  const { editSlideName } = useEditSlideName();

  const [setIntroductionVideo] = useMutation(SetSlideIntroductionVideoDocument);
  const [removeIntroductionVideo] = useMutation(RemoveSlideIntroductionVideoDocument, {
    update(cache, { data }) {
      if (!data?.removeSlideIntroductionVideo) return;

      const id = cache.identify(slide);

      if (!id) return;

      cache.modify({
        id,
        fields: {
          introductionVideo: () => ({
            src: null,
            filename: null,
            id: null,
          }),
        },
      });
    },
  });

  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSubtitleEditorOpen, setIsSubtitleEditorOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [generateSubtitles, { loading: isGeneratingSubtitles }] = useMutation(
    GenerateSlideSubtitlesDocument
  );
  const [updateSubtitles, { loading: isSavingSubtitles }] = useMutation(
    UpdateSlideSubtitlesDocument
  );

  // Subtitles are auto-generated in the background after an upload; the status drives the row indicator.
  const subtitleStatus = slide.introductionVideo?.subtitleStatus ?? null;
  const shouldPollSubtitles = !!slide.id && subtitleStatus === SubtitleStatus.Generating;
  // pollInterval must drop to 0 when idle, not just skip — skip stops the request but not the timer.
  useQuery(SlideByIdDocument, {
    variables: { id: slide.id ?? "" },
    skip: !shouldPollSubtitles,
    pollInterval: shouldPollSubtitles ? 10000 : 0,
    fetchPolicy: "network-only",
  });

  // The background run never touches this client, so toast the generating -> done/failed transition.
  const subtitleCount = slide.introductionVideo?.subtitles?.length ?? 0;
  const prevSubtitleStatusRef = useRef(subtitleStatus);
  useEffect(() => {
    const prev = prevSubtitleStatusRef.current;
    prevSubtitleStatusRef.current = subtitleStatus;
    if (prev !== SubtitleStatus.Generating) return;
    if (subtitleStatus === SubtitleStatus.Done) {
      // "done" with nothing produced = the video had no speech to caption.
      if (subtitleCount === 0) {
        toast("No speech found in this video — nothing to caption.", {
          id: "subtitleToast",
          icon: "ℹ️",
        });
      } else {
        toast.success("Subtitles generated!", { id: "subtitleToast" });
      }
    } else if (subtitleStatus === SubtitleStatus.Failed) {
      toast.error("Subtitle generation failed", { id: "subtitleToast" });
    }
  }, [subtitleStatus, subtitleCount]);

  const [updateSubtitleStyle] = useMutation<
    UpdateSlideSubtitleStyleMutation,
    UpdateSlideSubtitleStyleMutationVariables
  >(UpdateSlideSubtitleStyleDocument);

  const handleSaveSubtitleStyle = useCallback(
    ({ position, preset, fontScale, offset }: SubtitleStyleChange) => {
      if (!slide.id) return;
      updateSubtitleStyle({
        variables: { slideId: slide.id, position, preset, fontScale, offset },
        // Size and nudge are steppers: each click derives the next value from the
        // current one, so the cache must reflect this change before the server
        // answers — otherwise a quick second click recomputes from the stale value
        // and silently loses a step. Apollo reverts this if the mutation fails, so
        // the control never ends up showing something the database doesn't have.
        optimisticResponse: {
          updateSlideSubtitleStyle: {
            __typename: "Slide",
            ...slide,
            introductionVideo: {
              ...slide.introductionVideo,
              subtitlePosition: position,
              subtitlePreset: preset,
              subtitleFontScale: fontScale,
              subtitleOffset: offset,
            },
          },
        } as UpdateSlideSubtitleStyleMutation,
      }).catch(() => {
        toast.error("Failed to save caption style");
      });
    },
    [slide, updateSubtitleStyle]
  );

  const [updateVideoTrim] = useMutation<
    UpdateSlideVideoTrimMutation,
    UpdateSlideVideoTrimMutationVariables
  >(UpdateSlideVideoTrimDocument);

  const handleSaveTrim = useCallback(
    (start: number | null, end: number | null) => {
      if (!slide.id) return;
      updateVideoTrim({ variables: { slideId: slide.id, start, end } }).catch(() => {
        toast.error("Failed to save video trim");
      });
    },
    [slide.id, updateVideoTrim]
  );

  const handleGenerateSubtitles = async (mode?: SubtitleGenerationMode) => {
    if (!slide.id) return;
    toast.loading("Generating subtitles...", { id: "subtitleToast" });
    try {
      await generateSubtitles({ variables: { slideId: slide.id, mode } });
      toast.success("Subtitles generated!", { id: "subtitleToast" });
    } catch (err) {
      if (hasGraphQLErrorCode(err, ErrorCodes.SUBTITLE_UNAVAILABLE)) {
        // Not a failure — show the server's specific reason (no speech, no audio, too large…).
        toast(getGraphQLErrorMessage(err) ?? "This video can't be auto-captioned.", {
          id: "subtitleToast",
          icon: "ℹ️",
        });
      } else if (!toastAiCredits(err, "subtitleToast")) {
        toast.error("Failed to generate subtitles", { id: "subtitleToast" });
      }
    }
  };

  // slideData is typed as JSON (any) from GraphQL — narrow it safely
  const slideDataRecord = slide.slideData as Record<string, unknown> | null | undefined;
  const hasPronunciationSlideData =
    typeof slideDataRecord?.word === "string" && typeof slideDataRecord?.syllable === "string";

  // Reading-fluency and "other" slides can carry post-content sub-quizzes (slideData.subQuizzes).
  // Show a stacked-card peek behind the whole card as a visual hint that there's more attached.
  const subQuizzes = slideDataRecord?.subQuizzes;
  const hasSubQuizzes = Array.isArray(subQuizzes) && subQuizzes.length > 0;
  // Subslides get the same peek: both mean "more plays with this card".
  const showStackedPeek = hasSubQuizzes || subslideCount > 0;

  const handleSaveSubtitles = async (
    subtitles: { word: string; start: number; end: number; cueIndex?: number | null }[]
  ) => {
    if (!slide.id) return;
    try {
      await updateSubtitles({ variables: { slideId: slide.id, subtitles } });
      toast.success("Subtitles saved!");
      setIsSubtitleEditorOpen(false);
    } catch (_err) {
      toast.error("Failed to save subtitles");
    }
  };

  const handleClearSubtitles = async () => {
    if (!slide.id) return;
    try {
      await updateSubtitles({ variables: { slideId: slide.id, subtitles: [] } });
      toast.success("Subtitles cleared");
    } catch (_err) {
      toast.error("Failed to clear subtitles");
    }
  };

  const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !slide.id) return;

    setIsUploadingVideo(true);
    toast.loading("Uploading video...", { id: "introVideoToast" });

    try {
      await setIntroductionVideo({ variables: { slideId: slide.id, file } });
      toast.success("Introduction video set", { id: "introVideoToast" });
      toggleWarningModal("Generate subtitles for the video you just uploaded?", {
        onConfirm: () => void handleGenerateSubtitles(),
        onCancel: () => toggleWarningModal(false),
        confirmLabel: "Generate",
        cancelLabel: "Skip",
        title: "Subtitles",
      });
    } catch (_error) {
      toast.error("Failed to upload video", { id: "introVideoToast" });
    } finally {
      setIsUploadingVideo(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSaveName = () => {
    if (slideName.trim() && slideName !== slide.name && slide.id) {
      editSlideName(slide.id, slideName.trim());
    } else {
      setSlideName(slide.name || ""); // Reset if empty or unchanged
    }
    setIsEditingName(false);
  };

  const handleCancelEdit = () => {
    setSlideName(slide.name || ""); // Reset to original name
    setIsEditingName(false);
  };

  const handleCardClick = () => {
    if (!slide.id || isEditingName) return;

    if (isMergeMode) {
      // Master and non-source cards are inert while merging.
      if (mergeSelectDisabled) return;
      toggleSlide(slide.id, slide.isLinkedCopy ?? false, slide.sortOrder);
      return;
    }

    if (isSelectionMode) {
      toggleSlide(slide.id, slide.isLinkedCopy ?? false, slide.sortOrder);
      return;
    }

    if (url === "trash") return;

    navigate({ to: "/admin/slide/$slideId", params: { slideId: slide.id } });
  };

  // Linked copy logic
  const isLinkedCopy = slide.isLinkedCopy ?? false;
  const originalSlideId = slide.originalSlideId ?? null;
  const isHidden = slide.isHidden ?? false;

  const [getLinkedItemInfoQuery] = useLazyQuery(GetLinkedItemInfoDocument);

  const handleRedirectToOriginal = async () => {
    if (!originalSlideId) return;

    const linkedItem = await getLinkedItemInfoQuery({
      variables: { id: originalSlideId, model: ModelEnum.Slide },
    });

    const itemData = linkedItem.data?.linkedItemInfo;
    if (!itemData || !itemData.parentId) return;

    saveScrollTarget(`slide-${originalSlideId}`, itemData.parentId);
    const url: LinkProps["to"] = itemData.isTrash
      ? "/admin/lesson/$lessonId/trash"
      : "/admin/lesson/$lessonId";
    navigate({ to: url, params: { lessonId: itemData.parentId } });
  };

  const handleClickLinked = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!originalSlideId) return;

    toggleWarningModal("You will be redirected to the original item page. Continue?", {
      onCancel: () => toggleWarningModal(false),
      onConfirm: handleRedirectToOriginal,
    });
  };

  const handleRemoveIntroductionVideo = async () => {
    withToast(
      async () => removeIntroductionVideo({ variables: { slideId: slide.id } }),
      { loading: "Removing Video...", success: "Removed!", error: "Failed!" },
      "removeSlideIntroVidToast"
    );
  };

  if (viewMode === "list") {
    return (
      <div
        id={`slide-${slide.id}`}
        onClick={handleCardClick}
        className={`group relative flex items-center gap-3 px-4 py-3 rounded-lg border bg-surface cursor-pointer transition-all
          ${hasIncomplete ? "border-l-4 border-l-error-500 border-t border-r border-b border-gray-200 dark:border-gray-700" : "border border-gray-200 dark:border-gray-700"}
          ${isLinkedCopy ? "ring-2 ring-secondary-500" : ""}
          ${isSelected ? "ring-4 ring-primary-500" : ""}
          ${isMergeMaster ? "ring-2 ring-primary-500" : ""}
          ${mergeSelectDisabled && !isMergeMaster ? "opacity-50 cursor-not-allowed" : ""}
          ${isHidden ? "opacity-60" : ""}
          hover:bg-surface-hover`}
      >
        {/* Drag handle + number — fixed width; only this starts a drag */}
        <div
          ref={dragHandle?.ref}
          {...dragHandle?.attributes}
          {...dragHandle?.listeners}
          className="flex items-center gap-1.5 w-12 shrink-0 text-gray-400 select-none cursor-grab"
        >
          <MdDragIndicator className="w-5 h-5" />
          <span className="text-sm font-bold w-5 text-right">{slideNumber}</span>
        </div>

        {/* Slide name — fills remaining space */}
        <p className="flex-1 text-sm font-medium truncate min-w-0">{slide.name}</p>

        {/* Template — fixed width, truncated */}
        <div className="w-32 shrink-0">
          <span className="text-xs font-medium px-2 py-0.5 rounded border w-full text-center block truncate bg-primary-500/10 border-primary-500 text-primary-600 dark:text-primary-400">
            {templateLabel}
          </span>
        </div>

        {/* Domain — fixed width, truncated */}
        <div className="w-28 shrink-0" title={domainLabel}>
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded border w-full text-center block truncate ${domainColors}`}
          >
            {domainLabel}
          </span>
        </div>

        {/* Tags — narrow slot; slides carry one or two, so they wrap rather than truncate */}
        <div className="w-52 shrink-0">
          <SlideTagChips tagIds={slide.tagIds} />
        </div>

        {!isGeneratingAudio && (
          <div className="shrink-0">
            <GeneratingAudioBadge />
          </div>
        )}

        {/* Publication badge (grammar-form only) — compact, so a draft never looks published in list view */}
        {showPublicationBadge && (
          <div className="shrink-0">
            <GrammarFormPublicationBadge state={publicationState} compact />
          </div>
        )}

        {/* Status icons — fixed width, always reserves space (non-grammar-form slides) */}
        {!isGrammarFormSlide && (
          <div className="w-10 shrink-0 flex items-center justify-center gap-1">
            {hasIncomplete && <IoWarning className="w-4 h-4 text-error-500" />}
            {isHidden && <MdVisibilityOff className="w-4 h-4 text-gray-400" />}
          </div>
        )}

        {/* Action bar */}
        {inSelectionUI ? null : url === "trash" ? (
          <TrashCardActionBar slide={slide} id={slide.id || ""} type="slide" />
        ) : (
          <CardActionBar id={slide.id || ""} type="slide" slide={slide} />
        )}
      </div>
    );
  }

  return (
    <>
      <div id={`slide-${slide.id}`} className="relative flex flex-col">
        {showStackedPeek && (
          <>
            <div
              aria-hidden
              className="absolute inset-0 translate-x-3 translate-y-3 rounded-lg bg-surface border border-gray-300/70 dark:border-gray-800/70 shadow-sm opacity-60"
            />
            <div
              aria-hidden
              className="absolute inset-0 translate-x-1.5 translate-y-1.5 rounded-lg bg-surface border border-gray-300 dark:border-gray-800 shadow-sm opacity-85"
            />
          </>
        )}
        <Card
          hoverable={!inSelectionUI}
          className={`cursor-pointer relative z-10 ${
            isSubslide ? "ring-2 ring-amber-400 rounded-lg bg-amber-50/40 dark:bg-amber-950/20" : ""
          } ${
            isLinkedCopy ? "ring-2 ring-secondary-500 shadow-lg rounded-lg" : ""
          } ${isSelected ? "ring-4 ring-primary-500 shadow-xl" : ""} ${
            isMergeMaster ? "ring-2 ring-primary-500 shadow-lg" : ""
          } ${mergeSelectDisabled && !isMergeMaster ? "opacity-50" : ""} ${
            isGrammarFormSlide
              ? publicationState === "hidden"
                ? "opacity-75 border-dashed border-2 border-gray-400 dark:border-gray-600"
                : publicationState === "blocked_draft"
                  ? "opacity-75"
                  : ""
              : isHidden
                ? "opacity-75 border-dashed border-2 border-gray-400 dark:border-gray-600"
                : ""
          }`}
          aria-label={`Select slide ${slideNumber}: ${slide.name}`}
        >
          {hasIncomplete && (
            <div className="absolute top-0 left-0 bottom-0 w-1.5 bg-error-500 rounded-l-lg z-10" />
          )}
          {inSelectionUI && (
            <div className="absolute top-2 left-2 z-50 flex items-center gap-1.5">
              {isMergeMaster ? (
                <span className="px-2 py-0.5 text-xs font-semibold rounded bg-primary-500 text-white shadow">
                  Master
                </span>
              ) : (
                <>
                  <Checkbox
                    checked={isSelected}
                    disabled={mergeSelectDisabled}
                    onChange={() => {
                      if (slide.id && !mergeSelectDisabled) {
                        toggleSlide(slide.id, slide.isLinkedCopy ?? false, slide.sortOrder);
                      }
                    }}
                    size="lg"
                  />
                  {/* Selection-order badge — merge mode only, so the teacher sees the resulting
                      subQuizzes/passages order before committing. */}
                  {mergeSelectionOrder > 0 && (
                    <span
                      className="flex items-center justify-center w-5 h-5 rounded-full bg-primary-500 text-white text-xs font-bold shadow"
                      aria-label={`Merge order: ${mergeSelectionOrder}`}
                    >
                      {mergeSelectionOrder}
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          {isSelectionMode ? null : url === "trash" ? (
            <TrashCardActionBar slide={slide} id={slide.id || ""} type="slide" />
          ) : (
            <CardActionBar id={slide.id || ""} type="slide" slide={slide} />
          )}

          {isGeneratingAudio && (
            <div className="absolute top-3 left-3 z-50">
              <GeneratingAudioBadge />
            </div>
          )}

          {/* Linked Copy Badge - Top LEFT */}
          {isLinkedCopy && originalSlideId && (
            <button
              type="button"
              onClick={(e) => handleClickLinked(e)}
              className="absolute top-3 left-3 z-20"
            >
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-teal-500/80 backdrop-blur-sm text-white rounded-full shadow-md hover:shadow-lg transition-all duration-200">
                <MdLink className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold">Linked</span>
              </div>
            </button>
          )}

          {/* Publication Badge - Top RIGHT (grammar-form draft/hidden state) */}
          {showPublicationBadge && (
            <div className="absolute top-3 right-3 z-20">
              <GrammarFormPublicationBadge state={publicationState} />
            </div>
          )}

          {/* Hidden Badge - Top RIGHT (non-grammar-form slides) */}
          {!isGrammarFormSlide && isHidden && (
            <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1 bg-muted/80 backdrop-blur-sm text-white rounded-full shadow-md">
              <MdVisibilityOff className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">Hidden</span>
            </div>
          )}

          {/* Drag handle + slide number - Top RIGHT (hidden when a badge is shown or in trash/selection) */}
          {isSubslide && !isSelectionMode && (
            <div
              ref={dragHandle?.ref}
              {...dragHandle?.attributes}
              {...dragHandle?.listeners}
              className="absolute top-3 right-3 z-[40] flex items-center gap-1.5 px-2.5 py-1 bg-amber-500 text-white rounded-full shadow-md select-none cursor-grab"
            >
              <MdDragIndicator className="w-3.5 h-3.5 opacity-60" />
              <span className="text-xs font-semibold">Subslide {slideNumber}</span>
            </div>
          )}

          {!isSubslide &&
            !(isGrammarFormSlide ? showPublicationBadge : isHidden) &&
            !isSelectionMode &&
            url !== "trash" && (
              <div className="absolute top-3 right-3 z-[40] group/sort">
                <div
                  ref={dragHandle?.ref}
                  {...dragHandle?.attributes}
                  {...dragHandle?.listeners}
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-primary-900 backdrop-blur-sm text-white rounded-full shadow-md hover:shadow-lg hover:bg-gray-500 transition-all duration-200 cursor-grab select-none"
                >
                  <MdDragIndicator className="w-3.5 h-3.5 opacity-60 group-hover/sort:opacity-100 transition-opacity duration-200" />
                  <span className="text-xs font-semibold">{slideNumber}</span>
                </div>
                <div className="absolute top-full right-0 mt-2 pointer-events-none opacity-0 group-hover/sort:opacity-100 transition-opacity duration-200 delay-300">
                  <div className="relative bg-gray-900 text-white text-xs rounded-md px-2.5 py-1.5 shadow-lg whitespace-nowrap">
                    <span className="absolute -top-1 right-3 w-2 h-2 bg-gray-900 rotate-45 block" />
                    Drag card to reorder
                  </div>
                </div>
              </div>
            )}

          {/* Thumbnail */}
          <SlideThumbnail slide={slide} isHidden={isHidden} handleCardClick={handleCardClick} />

          {/* Bottom section — stopPropagation here covers all children */}
          <div className="p-4 pt-5 overflow-hidden">
            <div className="group/title flex items-center gap-2">
              <div className="flex-1">
                <textarea
                  value={slideName}
                  readOnly={!isEditingName}
                  rows={2}
                  className={`w-full resize-none bg-transparent border-none outline-none card-title
                  ${isEditingName ? "ring-2 ring-primary-500 rounded px-2 py-1 min-h-[3.5rem]" : "px-0 line-clamp-2 min-h-[3.5rem]"}`}
                  onChange={(e) => setSlideName(e.target.value)}
                  onClick={() => {
                    if (!isEditingName) setIsEditingName(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSaveName();
                    } else if (e.key === "Escape") {
                      handleCancelEdit();
                    }
                  }}
                  onBlur={handleSaveName}
                />
              </div>

              {!isEditingName ? (
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label="Edit slide name"
                  onClick={() => setIsEditingName(true)}
                  className="opacity-0 group-hover/title:opacity-100 transition-opacity duration-200 self-start"
                >
                  <MdEdit className="w-4 h-4" aria-hidden="true" />
                </IconButton>
              ) : (
                <div className="flex gap-1 flex-shrink-0 self-start">
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label="Save"
                    onClick={handleSaveName}
                    className="hover:bg-success-700/20"
                  >
                    <MdCheck className="w-4 h-4 text-success-500" />
                  </IconButton>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label="Cancel"
                    onClick={handleCancelEdit}
                    className="hover:bg-error-700/20"
                  >
                    <MdClose className="w-4 h-4 text-error-500" />
                  </IconButton>
                </div>
              )}
            </div>

            {/* Classification */}
            <div className="mb-4 space-y-2.5">
              <div className="space-y-1 overflow-hidden">
                <span className="label text-gray-600 dark:text-gray-400">Template:</span>
                <p className="label leading-relaxed px-3 py-2 rounded-md border bg-primary-500/10 border-primary-500 text-primary-600 dark:text-primary-400">
                  {templateLabel}
                </p>
              </div>

              <div className="space-y-1 overflow-hidden">
                <span className="label text-gray-600 dark:text-gray-400">Domain:</span>
                <p className={`label leading-relaxed px-3 py-2 rounded-md border ${domainColors}`}>
                  {domainLabel}
                </p>
              </div>

              {slide.tagIds?.length ? (
                <div className="space-y-1">
                  <span className="label text-gray-600 dark:text-gray-400">Tags:</span>
                  <SlideTagChips tagIds={slide.tagIds} />
                </div>
              ) : null}
            </div>

            {/* Introduction video */}
            <div className="mb-4">
              <label
                htmlFor={`introductionVideo-${slide.id}`}
                className="label text-gray-600 dark:text-gray-400 flex items-center gap-1.5 mb-1.5"
              >
                <FiFilm className="w-3.5 h-3.5 text-teal-500" />
                Introduction Video
              </label>

              <div className="flex items-center gap-2">
                <label
                  htmlFor={`introductionVideo-${slide.id}`}
                  className={`min-w-0 flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200
                    ${
                      !slide.introductionVideo.src
                        ? "bg-gray-200/30 border-gray-400"
                        : isUploadingVideo
                          ? "border-primary-500/90 bg-primary-500/20 opacity-60 cursor-not-allowed"
                          : "border-primary-500/90 bg-primary-500/20 cursor-pointer hover:bg-primary-500/10 hover:border-primary-500/70"
                    }`}
                >
                  <FiUploadCloud className="w-4 h-4 text-teal-500 shrink-0" />
                  <span className="label text-gray-700 dark:text-gray-400 truncate min-w-0">
                    {isUploadingVideo
                      ? "Uploading..."
                      : (slide.introductionVideo?.filename ?? "Click to add video")}
                  </span>
                </label>

                {slide.introductionVideo?.src && !isUploadingVideo && (
                  <>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label="Preview video"
                      onClick={() => setIsPreviewOpen(true)}
                      className="shrink-0 text-teal-500 hover:bg-teal-500/10 border border-teal-500/40 rounded-lg"
                    >
                      <FaPlay className="w-3 h-3" />
                    </IconButton>
                    <IconButton
                      variant="danger"
                      size="sm"
                      aria-label="Remove video"
                      onClick={handleRemoveIntroductionVideo}
                      className="shrink-0 text-teal-500 hover:bg-teal-500/10 border border-teal-500/40 rounded-lg"
                    >
                      <HiX className="w-3 h-3" />
                    </IconButton>
                  </>
                )}
              </div>

              {slide.introductionVideo?.src && !isUploadingVideo && (
                <>
                  {/* isGeneratingSubtitles too — the cache doesn't see the server's "generating" until the mutation resolves. */}
                  {(subtitleStatus === SubtitleStatus.Generating || isGeneratingSubtitles) && (
                    <p className="label mt-1.5 flex items-center gap-1.5 text-primary-600 dark:text-primary-400">
                      <span
                        aria-hidden
                        className="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin"
                      />
                      Generating subtitles…
                    </p>
                  )}
                  {subtitleStatus === SubtitleStatus.Failed && !isGeneratingSubtitles && (
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="label flex items-center gap-1.5 text-error-600 dark:text-error-400">
                        <IoWarning className="w-3.5 h-3.5 shrink-0" />
                        Subtitle generation failed
                      </span>
                      <button
                        type="button"
                        onClick={() => handleGenerateSubtitles()}
                        disabled={isGeneratingSubtitles}
                        className="shrink-0 rounded-md border border-error-500/40 px-2 py-0.5 text-xs font-semibold text-error-600 hover:bg-error-500/10 disabled:opacity-50 dark:text-error-400"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </>
              )}

              <input
                ref={fileInputRef}
                type="file"
                id={`introductionVideo-${slide.id}`}
                name="introductionVideo"
                accept="video/*"
                className="hidden"
                disabled={isUploadingVideo}
                onChange={handleVideoChange}
              />
            </div>

            {/* Asset Checklist section */}
            <div
              onClick={() => {
                if (showExpandButton) {
                  setIsWarningExpanded((prev) => !prev);
                }
              }}
              className={`relative p-3 rounded-lg border transition-all duration-200 ${
                showExpandButton ? "mb-3" : ""
              } ${hasIncomplete ? "bg-error-50 border-error-500" : "bg-success-100 border-success-500"}`}
            >
              <div
                className={`mb-1.5 font-medium overflow-hidden ${
                  hasIncomplete ? "text-error-700" : "text-success-700"
                }`}
              >
                {hasIncomplete ? (
                  <p className="flex gap-2 items-center">
                    <IoWarning color="#ff0000" fontSize={18} /> Asset Checklist
                  </p>
                ) : (
                  <p className="flex gap-2 items-center">
                    <FaRegCalendarCheck color="#175408" fontSize={18} /> All Complete
                  </p>
                )}
              </div>
              <div
                className={`label leading-relaxed transition-all duration-200 space-y-1 ${isWarningExpanded ? "" : "line-clamp-2 items-center"}`}
              >
                {slideWarnings.length === 0 ? (
                  <p className="line-clamp-2 min-h-[2lh] flex items-center text-success-700">
                    All required elements added
                  </p>
                ) : (
                  slideWarnings.map((warningBlock, blockIndex) => {
                    if (warningBlock.warningItem.length === 0) return null;

                    return (
                      <div key={`warning-block-${blockIndex + 1}`} className="space-y-0.5">
                        <p className="font-semibold text-xs text-gray-700">{warningBlock.title}:</p>
                        {warningBlock.warningItem.map((item, itemIndex) => (
                          <p
                            key={`item-${blockIndex}-${itemIndex + 1}`}
                            className={
                              item.status === "complete"
                                ? "text-success-700"
                                : item.status === "warning"
                                  ? "text-amber-600"
                                  : "text-error-700"
                            }
                          >
                            {item.status === "complete"
                              ? "✓"
                              : item.status === "warning"
                                ? "⚠"
                                : "•"}{" "}
                            {item.asset}
                            {item.current !== undefined && item.required !== undefined && (
                              <span className="text-xs">
                                {" "}
                                ({item.current}/{item.required})
                              </span>
                            )}
                          </p>
                        ))}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Expand/Collapse button s */}
              {showExpandButton && (
                <IconButton
                  variant="ghost"
                  size="xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsWarningExpanded((prev) => !prev);
                  }}
                  className={`absolute -bottom-3 left-1/2 -translate-x-1/2 z-10 rounded-full shadow-md ${
                    hasIncomplete
                      ? "bg-error-500/20 border border-error-400"
                      : "bg-success-500/20 border border-success-500"
                  }`}
                  aria-label={isWarningExpanded ? "Show less" : "Show more"}
                >
                  {isWarningExpanded ? (
                    <MdExpandLess className="w-4 h-4" />
                  ) : (
                    <MdExpandMore className="w-4 h-4" />
                  )}
                </IconButton>
              )}
            </div>

            <LinkedCopiesSection
              linkedCopies={slide.linkedCopies}
              type="Slide"
              originalItemId={slide.id}
            />

            {/* Always rendered on a main slide, disabled when the group is empty, so every card in
                the grid is the same height. */}
            {onToggleSubslides && !isSubslide && (
              <button
                type="button"
                disabled={subslideCount === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSubslides();
                }}
                className={`mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border transition-colors duration-200 ${
                  subslideCount === 0
                    ? "border-gray-300 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-default"
                    : "border-amber-400/70 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                }`}
              >
                {subslideCount === 0 ? (
                  <HiOutlineViewGrid className="w-4 h-4" />
                ) : isSubslidesExpanded ? (
                  <MdExpandLess className="w-4 h-4" />
                ) : (
                  <MdExpandMore className="w-4 h-4" />
                )}
                <span className="text-xs font-semibold">
                  {subslideCount === 0
                    ? "No subslides"
                    : `${subslideCount} ${subslideCount === 1 ? "subslide" : "subslides"}`}
                </span>
              </button>
            )}
          </div>
        </Card>
      </div>

      {isPreviewOpen &&
        slide.introductionVideo?.src &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70"
            onClick={() => setIsPreviewOpen(false)}
          >
            <div
              className="relative max-w-2xl w-full mx-4"
              onClick={(e) => e.stopPropagation()}
              // Portal events bubble through the React tree (→ SlideCard → the
              // @dnd-kit Sortable), so a pointerdown on the trim handles would
              // start dragging the card behind. Stop it at the modal boundary.
              onPointerDown={(e) => e.stopPropagation()}
            >
              <VideoPlayer
                src={slide.introductionVideo.src}
                subtitles={slide.introductionVideo?.subtitles ?? []}
                position={slide.introductionVideo?.subtitlePosition ?? null}
                preset={slide.introductionVideo?.subtitlePreset ?? null}
                fontScale={slide.introductionVideo?.subtitleFontScale ?? null}
                offset={slide.introductionVideo?.subtitleOffset ?? null}
                onClose={() => setIsPreviewOpen(false)}
                imageUrl={
                  slide.knowledgeArea === "pronunciation" &&
                  (slide.slideData as WordSlideData).isVideoWithImage
                    ? (slide.slideData as WordSlideData).image.src
                    : null
                }
                onGenerate={() => handleGenerateSubtitles(SubtitleGenerationMode.Standard)}
                {...(hasPronunciationSlideData && {
                  onGeneratePronunciation: () =>
                    handleGenerateSubtitles(SubtitleGenerationMode.Pronunciation),
                })}
                onEdit={() => setIsSubtitleEditorOpen(true)}
                onClear={handleClearSubtitles}
                onStyleChange={handleSaveSubtitleStyle}
                trimStart={slide.introductionVideo?.trimStart ?? null}
                trimEnd={slide.introductionVideo?.trimEnd ?? null}
                onTrimChange={handleSaveTrim}
                isGenerating={isGeneratingSubtitles || subtitleStatus === SubtitleStatus.Generating}
                subtitleFailed={subtitleStatus === SubtitleStatus.Failed}
              />
            </div>
          </div>,
          document.body
        )}

      {isSubtitleEditorOpen && slide.introductionVideo?.src && (
        <SubtitleEditor
          src={slide.introductionVideo.src}
          subtitles={slide.introductionVideo?.subtitles ?? []}
          position={slide.introductionVideo?.subtitlePosition ?? null}
          preset={slide.introductionVideo?.subtitlePreset ?? null}
          fontScale={slide.introductionVideo?.subtitleFontScale ?? null}
          offset={slide.introductionVideo?.subtitleOffset ?? null}
          isSaving={isSavingSubtitles}
          onSave={handleSaveSubtitles}
          onClose={() => setIsSubtitleEditorOpen(false)}
        />
      )}
    </>
  );
};
